import styled, { css, keyframes } from 'styled-components';

import themeColors from '@polyai/ui/styles/config/colors';
import themeSpacing from '@polyai/ui/styles/config/spacing';

export const Container = styled.div`
  position: relative;
  overflow: visible;
  display: grid;
  place-items: center;
`;

export const OrbWrap = styled.div`
  will-change: transform, filter;

  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) translate3d(0, 0, 0) scale(var(--orb-scale, 1));

  display: flex;
  align-items: center;
  justify-content: center;

  width: clamp(200px, 50vw, 300px);
  height: clamp(200px, 50vw, 300px);

  filter: blur(20px);
`;

export const OrbTheme = styled.div<{ $variant: 'cedar' | 'marin' }>`
    /* stylelint-disable-next-line color-no-hex */
    --orb-main: #9B97FF;
    --orb-accent: ${themeColors.textPrimaryInverse};

  ${p =>
    p.$variant === 'marin' &&
    css`
      --orb-main: ${themeColors.backgroundBrand};
      --orb-accent: ${themeColors.textPrimaryInverse};
    `}
`;

export const Blob = styled.div`
  --dx: var(--orb-dx, 0px);
  --dy: var(--orb-dy, 0px);

  will-change: transform;

  position: absolute;
  top: 50%;
  left: 50%;

  opacity: 0.85;
  border-radius: 50%;
`;

const breathe1 = keyframes`
  0% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1); }
  33% { transform: translate(calc(-50% + var(--dx) + 8px), calc(-50% + var(--dy) + 12px)) scale(1.05); }
  66% { transform: translate(calc(-50% + var(--dx) - 5px), calc(-50% + var(--dy) - 8px)) scale(0.98); }
  100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1); }
`;

const breathe2 = keyframes`
  0% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1); }
  40% { transform: translate(calc(-50% + var(--dx) - 10px), calc(-50% + var(--dy) + 6px)) scale(0.97); }
  80% { transform: translate(calc(-50% + var(--dx) + 4px), calc(-50% + var(--dy) - 10px)) scale(1.03); }
  100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1); }
`;

const breathe3 = keyframes`
  0% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1); }
  25% { transform: translate(calc(-50% + var(--dx) + 6px), calc(-50% + var(--dy) - 12px)) scale(1.06); }
  75% { transform: translate(calc(-50% + var(--dx) - 8px), calc(-50% + var(--dy) + 4px)) scale(0.96); }
  100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1); }
`;

const breathe4 = keyframes`
  0% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1); }
  30% { transform: translate(calc(-50% + var(--dx) - 4px), calc(-50% + var(--dy) + 10px)) scale(0.99); }
  70% { transform: translate(calc(-50% + var(--dx) + 10px), calc(-50% + var(--dy) - 6px)) scale(1.02); }
  100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1); }
`;

export const Blob1 = styled(Blob)`
  width: clamp(107px, 26.67vw, 160px);
  height: clamp(107px, 26.67vw, 160px);
  background-color: var(--orb-main);
  animation: ${breathe1} 6s infinite alternate ease-in-out;
`;

export const Blob2 = styled(Blob)`
  width: clamp(67px, 16.67vw, 100px);
  height: clamp(67px, 16.67vw, 100px);
  background-color: var(--orb-accent);
  animation: ${breathe2} 3s infinite alternate-reverse ease-in-out;
`;

export const Blob3 = styled(Blob)`
  width: clamp(127px, 31.67vw, 190px);
  height: clamp(127px, 31.67vw, 190px);
  background-color: var(--orb-main);
  animation: ${breathe3} 4s infinite alternate ease-in-out;
`;

export const Blob4 = styled(Blob)`
  --dx: 8px;
  --dy: -8px;

  width: clamp(67px, 16.67vw, 100px);
  height: clamp(67px, 16.67vw, 100px);

  opacity: 0.3;
  background-color: var(--orb-accent);

  animation: ${breathe4} 5s infinite alternate-reverse ease-in-out;
`;

export const Controls = styled.div`
  user-select: none;

  position: absolute;
  bottom: ${themeSpacing.sm3};

  display: flex;
  flex-direction: column;
  gap: ${themeSpacing.xs2};
  align-items: center;
`;
